/**
 * MatchBreakdown — compact expandable breakdown of a talent-vs-job match score.
 *
 * Renders three signal lines:
 *   1. Skills matched: "{matched}/{total} required skills matched"
 *   2. YoE delta:      "{delta} yr experience gap" (signed +/-)
 *   3. Education:      "Education match" (only when educationMatch is true)
 *
 * Uses i18n keys: talentPool.addFromPool.scoreBreakdown.{skills,yoe,education}
 * Typography: body-sm, no gradients, no emojis.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, TrendingUp, GraduationCap } from 'lucide-react';
import { Typography } from '@/components/ui/atoms/Typography';
import type { TalentMatchScoreBreakdown } from '@/types/talent.types';

export interface MatchBreakdownProps {
  /** The match score breakdown to render */
  breakdown: TalentMatchScoreBreakdown;
  /** Additional className */
  className?: string;
}

export const MatchBreakdown: React.FC<MatchBreakdownProps> = ({ breakdown, className = '' }) => {
  const { t } = useTranslation('talentPool');

  // yoeDelta is signed MONTHS (locked backend shape); the label is in years.
  const yoeYears = Math.round(breakdown.yoeDelta / 12);
  const yoeDeltaLabel = yoeYears >= 0 ? `+${yoeYears}` : String(yoeYears);

  return (
    <div className={`flex flex-col gap-1.5 py-2 ${className}`}>
      {/* Skills signal */}
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-dark-invirtus/50" aria-hidden="true" />
        <Typography variant="body-sm" color="secondary">
          {t('addFromPool.scoreBreakdown.skills', {
            matched: breakdown.skillsMatched,
            total: breakdown.skillsTotal,
          })}
        </Typography>
      </div>

      {/* YoE delta signal */}
      <div className="flex items-center gap-2">
        <TrendingUp className="h-3.5 w-3.5 shrink-0 text-dark-invirtus/50" aria-hidden="true" />
        <Typography variant="body-sm" color="secondary">
          {t('addFromPool.scoreBreakdown.yoe', { delta: yoeDeltaLabel })}
        </Typography>
      </div>

      {/* Education signal — only when matched */}
      {breakdown.educationMatch && (
        <div className="flex items-center gap-2">
          <GraduationCap className="h-3.5 w-3.5 shrink-0 text-dark-invirtus/50" aria-hidden="true" />
          <Typography variant="body-sm" color="secondary">
            {t('addFromPool.scoreBreakdown.education')}
          </Typography>
        </div>
      )}
    </div>
  );
};

MatchBreakdown.displayName = 'MatchBreakdown';
